import { isNavigationFailure } from 'vue-router';

import type { ContentLockedRouteVerdict } from '~/composables/useContentLockedRoute';

/**
 * The second half of the capability route gate: `02.capability-route-protection` decides whether
 * the destination is locked, and this publishes that decision once vue-router has actually moved.
 *
 * Splitting the two is what keeps a locked page from mounting on the way out of it. `beforeEach`
 * fires while `router.currentRoute` still names the locked path, so a lock cleared there leaves
 * the layout rendering `<slot/>` — and `<NuxtPage>` under it still resolving to the page being
 * left — long enough for the locked page's queries to run and be refused against the destination.
 * `afterEach` runs in the same synchronous step that finalises `currentRoute`, so the lock and the
 * route the layout matches it against always change together.
 *
 * The commit is keyed by path because a verdict and the navigation it belongs to can come apart:
 * a navigation vue-router cancels still runs `afterEach` (with a failure), and by then the gate
 * may already have published the *next* navigation's verdict over it. Committing either one would
 * publish a lock for a route the browser is not on.
 *
 * The setup-time commit is a defensive guard for a verdict already pending when this plugin
 * registers its hook. None of Nuxt's initial navigations runs the gate before the hook exists
 * (the middleware guard is registered after the router plugin's own initial push), so it only
 * matters if that ordering ever changes.
 */
export default defineNuxtPlugin(() => {
  const lockedRoute = useContentLockedRoute();
  const pendingLockedRoute = usePendingContentLockedRoute();

  const commit = (verdict: ContentLockedRouteVerdict) => {
    lockedRoute.value = verdict.lock;
    pendingLockedRoute.value = null;
  };

  // A verdict already pending at setup can only belong to the navigation that just happened.
  const initialVerdict = pendingLockedRoute.value;
  if (initialVerdict) {
    commit(initialVerdict);
  }

  useRouter().afterEach((to, _from, failure) => {
    const verdict = pendingLockedRoute.value;
    if (!verdict || isNavigationFailure(failure) || verdict.path !== to.path) {
      return;
    }

    commit(verdict);
  });
});
